import { type ReactElement } from 'react';
import clsx from 'clsx';
import { TdsIcon } from '@scania/tegel-react';
import { type VehicleCycledEnergyRecord } from '../../services/battery-monitoring-api/types/battery-monitoring-api';
import TruckIcon from './TruckIcon';
import BusIcon from './BusIcon';
import Row from './Row';
import styles from './style.module.scss';
import { localizeDate } from '@/utils/date';
import { formatMaxOneDecimal } from '@/utils/number-formatters';

interface VehicleDetailProperties {
  readonly selectedVehicle: VehicleCycledEnergyRecord;
}

const getStateOfHealthLabel = (isStateOfHealthEstimated?: boolean): string =>
  isStateOfHealthEstimated === true ? 'estimated' : 'telemetry';

interface SpecificationAlertProperties {
  readonly label: string;
}

const SpecificationAlert = ({ label }: SpecificationAlertProperties): ReactElement => (
  <div className={styles['alert-row']}>
    <TdsIcon name="warning" size="20px" className="tds-text-warning" />
    <span>{label}</span>
  </div>
);

const Specification = ({ selectedVehicle }: VehicleDetailProperties): ReactElement => {
  const hasFooterAlerts = selectedVehicle.stateOfHealthAlert || selectedVehicle.cycledEnergyPerKmAlert;

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.rows}>
          <h3 className={styles.heading}>Details</h3>
          <Row
            iconName={selectedVehicle.propulsion === 'PHEV' ? 'engine' : 'flash'}
            value={selectedVehicle.propulsion ?? 'BEV'}
          />
          <Row
            iconName="pulse"
            value={
              typeof selectedVehicle.stateOfHealthRatio === 'number'
                ? formatMaxOneDecimal(selectedVehicle.stateOfHealthRatio * 100)
                : undefined
            }
            suffix={` % SoH (${getStateOfHealthLabel(selectedVehicle.isStateOfHealthEstimated)})`}
          />
          <Row iconName="engine" value={selectedVehicle.electricPropulsionUnitType ?? undefined} />
          <Row
            iconName="flash"
            value={typeof selectedVehicle.batteryCapacity === 'number' ? selectedVehicle.batteryCapacity : undefined}
            suffix=" kWh Battery (installed)"
          />
          <Row
            iconName="battery"
            value={typeof selectedVehicle.socWindow === 'number' ? selectedVehicle.socWindow : undefined}
            suffix="% SoC window"
          />
          <Row iconName="trailer" value={selectedVehicle.configuration || undefined} />
          <Row
            iconName="calendar"
            prefix="Assembled: "
            value={selectedVehicle.assemblyDate ? localizeDate(new Date(selectedVehicle.assemblyDate)) : undefined}
          />
          <Row
            iconName="calendar"
            prefix="Latest readout: "
            value={
              selectedVehicle.latestVehicleDataTimestamp
                ? localizeDate(new Date(selectedVehicle.latestVehicleDataTimestamp))
                : undefined
            }
          />
        </div>
        <div className={styles['vehicle-type-icon']}>
          {selectedVehicle.vehicleType === 'Truck' ? <TruckIcon /> : <BusIcon />}
        </div>
      </div>
      <div
        className={clsx(styles.footer, { [styles['footer-with-alerts']]: hasFooterAlerts })}
        data-testid="specification-footer"
      >
        {selectedVehicle.stateOfHealthAlert ? <SpecificationAlert label="Low state of health" /> : null}
        {selectedVehicle.cycledEnergyPerKmAlert ? <SpecificationAlert label="High cycled energy per km" /> : null}
      </div>
    </div>
  );
};

export default Specification;
